import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Clock, TrendingUp, MonitorOff, DollarSign, ShieldCheck, FileText, Zap, Building2 } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.5
  }
};
export function SalesPage() {
  return <div className="min-h-screen bg-slate-50 selection:bg-amber-200 selection:text-slate-900" data-id="element-12">
      {/* WARNING BANNER */}
      <div className="bg-red-600 px-4 py-2 text-center text-sm font-bold tracking-wider text-white sm:text-base" data-id="element-13">
        ATTENTION COMMERCIAL CLEANING OWNERS
      </div>

      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-slate-900 px-4 pt-16 pb-20 sm:px-6 lg:px-8 lg:pt-24 lg:pb-28" data-id="element-14">
        <img src="https://cdn.magicpatterns.com/uploads/sRf4hCy2Q6JqgwkfkSBy2M/AD24E674-1736-4F55-A1E3-3592EF8703CB.png" alt="Professional cleaning company owner holding a commercial cleaning services proposal in a corporate lobby" className="absolute inset-0 h-full w-full object-cover object-[28%_center] sm:object-center" data-id="element-15" />
        <div className="absolute inset-0 bg-slate-900/35" data-id="element-16"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/30 via-slate-900/40 to-slate-900/85" data-id="element-17"></div>

        <div className="relative mx-auto max-w-4xl text-center" data-id="element-18">
          <motion.h1 initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="text-4xl font-black leading-tight text-white sm:text-5xl md:text-6xl lg:text-7xl" data-id="element-19">
            STOP LOSING <span className="text-amber-400" data-id="element-20">$50k CONTRACTS</span>{' '}
            TO COMPETITORS WHO AREN'T EVEN BETTER THAN YOU.
          </motion.h1>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} className="mx-auto mt-8 max-w-2xl text-xl font-medium leading-relaxed text-slate-300 sm:text-2xl" data-id="element-21">
            The Step-by-Step Guide to Winning Commercial Cleaning Contracts
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.5
        }} className="mt-10 flex justify-center" data-id="element-22">
            <CTAButton data-id="element-23" />
          </motion.div>
        </div>
      </section>

      {/* THE PROBLEM STORY */}
      <section className="px-4 py-20 sm:px-6 lg:px-8" data-id="element-24">
        <div className="mx-auto max-w-3xl" data-id="element-25">
          <motion.div {...fadeIn} className="prose prose-lg prose-slate mx-auto md:prose-xl" data-id="element-26">
            <div className="mb-8 flex items-center gap-4 rounded-2xl bg-red-50 p-6 text-red-900 border border-red-100" data-id="element-27">
              <AlertTriangle className="h-12 w-12 shrink-0 text-red-500" data-id="element-28" />
              <p className="m-0 font-bold leading-snug" data-id="element-29">
                "Overworked" Mike was losing 4 out of 5 bids.
              </p>
            </div>

            <p data-id="element-30">
              It wasn't because he was a bad cleaner. In fact, he was the best
              in town.
              <strong data-id="element-31"> The problem?</strong> He was sitting in his truck,
              exhausted after a 12-hour shift, trying to write a "bid" on a
              messy piece of paper or a poorly formatted email.
            </p>

            <p data-id="element-32">
              By the time he sent it 3 days later, a bigger company had already
              moved in. They didn't have better cleaners—
              <strong data-id="element-33">they just had a better proposal.</strong>
            </p>

            <div className="my-12 border-l-4 border-slate-900 bg-slate-100 p-8" data-id="element-34">
              <p className="m-0 text-xl font-bold italic text-slate-900" data-id="element-35">
                "In the US Commercial Market, Facility Managers don't hire the
                'best cleaner.' They hire the company that looks the most
                Professional, Reliable, and Corporate."
              </p>
            </div>

            <h2 className="text-center text-3xl font-black text-slate-900 sm:text-4xl" data-id="element-36">
              If you look small, you stay small.
            </h2>
          </motion.div>
        </div>
      </section>

      {/* THE SOLUTION */}
      <section className="bg-blue-900 px-4 py-20 text-white sm:px-6 lg:px-8" data-id="element-37">
        <div className="mx-auto max-w-5xl" data-id="element-38">
          <motion.div {...fadeIn} className="text-center" data-id="element-39">
            <span className="mb-4 inline-block rounded-full bg-blue-800 px-4 py-1.5 text-sm font-bold tracking-wider text-blue-200" data-id="element-40">
              THE PIVOT
            </span>
            <h2 className="mb-6 text-4xl font-black sm:text-5xl lg:text-6xl" data-id="element-41">
              INTRODUCING: THE{' '}
              <span className="text-amber-400" data-id="element-42">
                COMMERCIAL CLEANING BID BLUEPRINT
              </span>
            </h2>
            <p className="mx-auto max-w-3xl text-xl text-blue-100 sm:text-2xl" data-id="element-43">
              This is not a "course." It's a step-by-step guide that walks you
              through winning high-ticket commercial cleaning contracts.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.94
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="mx-auto mt-16 max-w-4xl px-2 sm:px-6" data-id="element-44">
            <img src="https://cdn.magicpatterns.com/uploads/vxtRFR9YST7k5S58f4nx12/1A8AA47E-1C65-4B88-8582-79437F4AB4D5.png" alt="The Commercial Cleaning Bid Guide shown as a book, laptop, tablet, and phone mockup" className="h-auto w-full rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/10" data-id="element-45" />
          </motion.div>

          <div className="mt-16 grid gap-8 md:grid-cols-2" data-id="element-46">
            <motion.div {...fadeIn} className="rounded-2xl border border-blue-700 bg-blue-800/50 p-8 backdrop-blur-sm" data-id="element-47">
              <div className="mb-6 inline-flex rounded-xl bg-blue-600 p-4" data-id="element-48">
                <FileText className="h-8 w-8 text-white" data-id="element-49" />
              </div>
              <h3 className="mb-4 text-2xl font-bold" data-id="element-50">
                The Step-by-Step Proposal Framework
              </h3>
              <p className="text-blue-100" data-id="element-51">
                Stop spending your entire Sunday writing bids. The guide walks
                you through turning your messy walkthrough notes into a 5-page,
                high-end corporate proposal.
              </p>
              <ul className="mt-6 space-y-3" data-id="element-52">
                {['Executive Summary', 'Detailed Scope of Work', 'Quality Control'].map((item, i) => <li key={i} className="flex items-center gap-3 text-blue-50" data-id="element-53">
                    <CheckCircle2 className="h-5 w-5 text-amber-400" data-id="element-54" />
                    <span className="font-medium" data-id="element-55">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div {...fadeIn} className="flex flex-col justify-center rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent p-8" data-id="element-56">
              <h3 className="mb-4 text-3xl font-black text-amber-400" data-id="element-57">
                The Result?
              </h3>
              <p className="text-2xl font-bold leading-tight text-white" data-id="element-58">
                You look like a Million-Dollar company even if you're a one-man
                show.
              </p>
              <div className="mt-8" data-id="element-59">
                <CTAButton subtext="" className="!items-start" data-id="element-60" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="px-4 py-20 sm:px-6 lg:px-8" data-id="element-61">
        <div className="mx-auto max-w-5xl" data-id="element-62">
          <motion.div {...fadeIn} className="mb-16 text-center" data-id="element-63">
            <h2 className="text-4xl font-black text-slate-900 sm:text-5xl" data-id="element-64">
              WHY YOU NEED THIS <span className="text-red-600" data-id="element-65">TODAY</span>
            </h2>
          </motion.div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-66">
            {[{
            icon: <Zap className="h-8 w-8 text-amber-500" data-id="element-67" />,
            title: 'Speed Wins',
            desc: 'Send your proposal before you even leave the parking lot of the walkthrough.'
          }, {
            icon: <Building2 className="h-8 w-8 text-blue-500" data-id="element-68" />,
            title: 'High-Ticket Focus',
            desc: 'Designed specifically for Banks, Medical Offices, and Corporate Buildings.'
          }, {
            icon: <MonitorOff className="h-8 w-8 text-green-500" data-id="element-69" />,
            title: 'Zero Tech Skills',
            desc: 'If you can read, you can follow this system.'
          }, {
            icon: <DollarSign className="h-8 w-8 text-slate-700" data-id="element-70" />,
            title: 'No Monthly Fees',
            desc: 'Pay once, use it for every bid for the next 5 years.'
          }].map((benefit, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1,
            duration: 0.5
          }} className="rounded-2xl bg-white p-8 shadow-xl shadow-slate-200/50 border border-slate-100" data-id="element-71">
                <div className="mb-6 inline-flex rounded-xl bg-slate-50 p-4" data-id="element-72">
                  {benefit.icon}
                </div>
                <h3 className="mb-3 text-xl font-bold text-slate-900" data-id="element-73">
                  {benefit.title}
                </h3>
                <p className="text-slate-600" data-id="element-74">{benefit.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="bg-slate-900 px-4 py-20 sm:px-6 lg:px-8" data-id="element-75">
        <div className="mx-auto max-w-4xl" data-id="element-76">
          <motion.div {...fadeIn} className="relative overflow-hidden rounded-3xl border-2 border-amber-400 bg-slate-800 p-8 text-center sm:p-12" data-id="element-77">
            <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-amber-400/10 blur-3xl" data-id="element-78"></div>
            <div className="absolute -left-12 -bottom-12 h-40 w-40 rounded-full bg-amber-400/10 blur-3xl" data-id="element-79"></div>

            <ShieldCheck className="mx-auto mb-6 h-20 w-20 text-amber-400" data-id="element-80" />
            <h2 className="mb-6 text-3xl font-black text-white sm:text-4xl" data-id="element-81">
              THE "BID-WINNER" GUARANTEE
            </h2>
            <p className="mx-auto max-w-2xl text-lg leading-relaxed text-slate-300 sm:text-xl" data-id="element-82">
              Download the guide. Use it for your next walkthrough. If you don't
              feel 10x more confident sending that proposal—and if it doesn't
              look 100x more professional than your old bids—just email us
              within 7 days for a full refund.{' '}
              <strong data-id="element-83">No questions asked.</strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 py-24 sm:px-6 lg:px-8 bg-white" data-id="element-84">
        <div className="mx-auto max-w-3xl text-center" data-id="element-85">
          <motion.div {...fadeIn} data-id="element-86">
            <h2 className="mb-8 text-4xl font-black text-slate-900 sm:text-5xl" data-id="element-87">
              Ready to start winning like the Big Dogs?
            </h2>
            <div className="flex justify-center" data-id="element-88">
              <CTAButton data-id="element-89" />
            </div>
            <p className="mt-6 text-sm font-medium text-slate-500" data-id="element-90">
              Secure checkout. Instant delivery to your email.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-800 bg-slate-900 px-4 py-10 sm:px-6 lg:px-8" data-id="element-91">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 text-center sm:flex-row sm:justify-between sm:text-left" data-id="element-92">
          <p className="text-sm text-slate-400" data-id="element-93">
            &copy; {new Date().getFullYear()} Commercial Cleaning Bid Blueprint.
            All rights reserved.
          </p>
          <nav aria-label="Footer" className="flex items-center gap-6" data-id="element-94">
            <a href="#" className="text-sm font-medium text-slate-400 transition-colors hover:text-amber-400" data-id="element-95">
              Terms &amp; Conditions
            </a>
            <a href="#" className="text-sm font-medium text-slate-400 transition-colors hover:text-amber-400" data-id="element-96">
              Privacy Policy
            </a>
          </nav>
        </div>
      </footer>
    </div>;
}